/**
 * Privacy-preserving analytics.
 *
 * The strategy document names a single leading indicator: *"percentage of
 * sessions that run an analysis tool — below 30% means repositioning."* There was
 * no way to measure it, which made the entire product thesis unfalsifiable.
 *
 * Four rules make measurement compatible with the privacy promise:
 *
 *   1. **Never on `/resume-builder`.** No event fires from the page where resume
 *      content exists. The claim being protected is about resume data, not page
 *      views.
 *   2. **No resume content, ever.** Events carry a name and a route. There is no
 *      payload parameter, by construction — the function signature cannot express
 *      one.
 *   3. **Nothing loads unless configured.** With no endpoint set, this module
 *      makes zero network requests and injects zero script tags, so a self-hosted
 *      fork stays request-free by default.
 *   4. **Do Not Track and Global Privacy Control are honoured.** Both are checked
 *      before anything is sent.
 *
 * Configure with a cookieless, IP-anonymising endpoint (Plausible, Umami,
 * GoatCounter). Update `/privacy` if you enable it — the page currently states
 * that nothing is collected, and that must remain true of whatever ships.
 */

import { SITE } from '@/config/site'

export type AnalyticsEvent =
  | 'tool_opened'
  | 'analysis_run'
  | 'sample_loaded'
  | 'pdf_exported'
  | 'json_exported'
  | 'resume_imported'
  | 'template_changed'
  | 'share_open'
  | 'share_platform_selected'
  | 'share_completed'
  | 'share_copy_link'
  | 'share_prompt_shown'
  | 'share_prompt_clicked'
  | 'share_prompt_dismissed'

const optedOut = (): boolean => {
  if (typeof navigator === 'undefined') return true
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean; msDoNotTrack?: string }
  if (nav.globalPrivacyControl === true) return true
  if (nav.doNotTrack === '1' || nav.msDoNotTrack === '1') return true
  if (typeof window !== 'undefined' && (window as { doNotTrack?: string }).doNotTrack === '1') {
    return true
  }
  return false
}

const enabled = (): boolean =>
  Boolean(SITE.analyticsEndpoint) &&
  typeof window !== 'undefined' &&
  !optedOut() &&
  // The builder is the one route that holds resume content. Nothing is reported
  // from it — not even a page view.
  !window.location.pathname.startsWith('/resume-builder')

/**
 * Records an event. Deliberately takes no payload argument: there is no way for
 * a caller to attach resume content even by mistake.
 */
export const track = (event: AnalyticsEvent): void => {
  if (!enabled()) return
  try {
    const body = JSON.stringify({ event, path: window.location.pathname })
    // `sendBeacon` survives navigation and cannot block the main thread.
    navigator.sendBeacon?.(SITE.analyticsEndpoint, new Blob([body], { type: 'application/json' }))
  } catch {
    // Measurement must never break the product.
  }
}
